import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import { MdOutlinePerson } from "react-icons/md";
import { Link } from "react-router-dom-v5-compat";

import { CustomerDoc } from "@megaron/docs-contracts";

type CrmCustomerCardVariant = "default" | "reporter";

type Props = {
  customer: CustomerDoc;
  onDetach?: () => void;
  caption?: string;
  variant?: CrmCustomerCardVariant;
};

export const CrmCustomerCard: React.FC<Props> = ({
  customer,
  onDetach,
  caption = "Klient w CRM",
  variant = "default",
}) => {
  const theme = useTheme();
  const [isDetachDialogOpen, setIsDetachDialogOpen] = useState(false);
  const isReporter = variant === "reporter";

  return (
    <div
      css={{
        borderRadius: theme.smallBorderRadius,
        background: isReporter ? theme.colors.primaryLight : "white",
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
        border: isReporter ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
      }}
    >
      <Link
        to={`/crm/customers/id/${customer.uuid}`}
        css={{
          display: "flex",
          flex: "1 0 0",
          minWidth: 0,
          textDecoration: "none",
          "&:hover": { opacity: 0.85 },
        }}
      >
        <div
          css={{
            color: "white",
            background: theme.colors.primary,
            padding: "0.5rem 0.875rem",
            alignSelf: "stretch",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1px solid ${theme.colors.primary}`,
          }}
        >
          <MdOutlinePerson size={18} />
        </div>
        <div
          css={{
            minWidth: 0,
            padding: "0.5rem 0.75rem",
            display: "flex",
            flex: "1 0 0",
            flexDirection: "column",
          }}
        >
          <div css={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem" }}>
            <div css={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <span
                css={{
                  fontSize: "0.75rem",
                  color: theme.colors.secondaryText,
                  fontWeight: isReporter ? 600 : 400,
                }}
              >
                {caption}
              </span>
              <span css={{ fontSize: "0.875rem", fontWeight: 700, color: theme.colors.primary }}>
                {customer.name}
              </span>
            </div>
            <div css={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px", flexShrink: 0 }}>
              {customer.phoneNumber?.trim() && (
                <span css={{ fontSize: "0.75rem", color: theme.colors.primary }}>
                  {customer.phoneNumber}
                </span>
              )}
              {customer.email && (
                <span css={{ fontSize: "0.75rem", color: theme.colors.primary }}>
                  {customer.email}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
      {onDetach && (
        <button
          type="button"
          onClick={() => setIsDetachDialogOpen(true)}
          css={{
            background: "none",
            border: "none",
            borderLeft: `1px solid ${theme.colors.border}`,
            padding: "0 0.625rem",
            cursor: "pointer",
            fontSize: "16px",
            lineHeight: 1,
            color: theme.colors.secondaryText,
            "&:hover": { color: theme.colors.danger },
          }}
          title="Odepnij klienta"
        >
          ×
        </button>
      )}
      {isDetachDialogOpen && (
        <Dialog header="Odpiąć klienta z CRM?" onClose={() => setIsDetachDialogOpen(false)} placement="center">
          <div css={{ display: "flex", width: "100%", gap: "0.5rem" }}>
            <Button css={{ width: "100%" }} variant="outline" onClick={() => setIsDetachDialogOpen(false)}>
              Nie
            </Button>
            <Button
              css={{ width: "100%" }}
              variant="primary"
              onClick={() => {
                onDetach?.();
                setIsDetachDialogOpen(false);
              }}
            >
              Tak
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
};
